import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, Copy, Edit3, MapPin, Plus, Sparkles, StickyNote, Users,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Field, MatchRing, PageHeader, StatusBadge, SkillChip } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import JobForm from "@/components/forms/JobForm";
import { SelectField } from "@/components/forms/Fields";
import { InterviewForm } from "@/components/forms/OpsForms";

const FUNNEL = ["Applications", "Screened", "Shortlisted", "Submitted", "Interview", "Offer", "Joined"];

function PipelineBoard({ jobId, onChanged, canMove }) {
  const [board, setBoard] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkStage, setBulkStage] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/pipeline`);
      setBoard(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (ids, stage) => {
    if (!ids.length) return;
    try {
      await api.post(`/jobs/${jobId}/pipeline/move`, { application_ids: ids, stage });
      toast.success(`Moved ${ids.length} candidate${ids.length > 1 ? "s" : ""} to ${stage}`);
      setSelected([]);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!board)
    return (
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-80 w-72 shrink-0 rounded-md bg-slate-100" />
        ))}
      </div>
    );

  const order = board.stage_order.filter((s) => (board.stages[s] || []).length > 0 || !["On Hold"].includes(s));

  return (
    <div className="space-y-4">
      {canMove && selected.length > 0 && (
        <div className="surface flex flex-wrap items-center gap-3 rounded-md p-3" data-testid="pipeline-bulk-bar">
          <span className="text-xs text-slate-600">{selected.length} selected</span>
          <SelectField value={bulkStage} onChange={setBulkStage} options={board.stage_order} placeholder="Move to stage" testid="pipeline-bulk-stage" className="w-52" />
          <Button
            size="sm"
            disabled={!bulkStage}
            onClick={() => move(selected, bulkStage)}
            data-testid="pipeline-bulk-move"
            className="h-8 rounded-full bg-blue-600 text-xs hover:bg-blue-500"
          >
            Move selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="h-8 text-xs text-slate-500">
            Clear
          </Button>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto pb-4 thin-scroll" data-testid="pipeline-board">
        {order.map((stage) => {
          const cards = board.stages[stage] || [];
          return (
            <div
              key={stage}
              onDragOver={(e) => canMove && e.preventDefault()}
              onDrop={() => {
                if (!canMove || !dragging) return;
                if (dragging.stage !== stage) move([dragging.application_id], stage);
                setDragging(null);
              }}
              data-testid={`pipeline-column-${stage.replace(/\s/g, "-")}`}
              className="w-72 shrink-0 rounded-md border border-slate-200 bg-slate-100"
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
                <p className="text-[11px] font-medium tracking-wide text-slate-600">{stage}</p>
                <span className="font-mono-num rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">
                  {cards.length}
                </span>
              </div>
              <div className="max-h-[62vh] space-y-2 overflow-y-auto p-2 thin-scroll">
                {cards.length === 0 ? (
                  <p className="px-2 py-6 text-center text-[11px] text-slate-400">Drop candidates here</p>
                ) : (
                  cards.map((c) => (
                    <div
                      key={c.application_id}
                      draggable={canMove}
                      onDragStart={() => setDragging(c)}
                      data-testid="pipeline-card"
                      className="group rounded border border-slate-200 bg-white p-3 transition-colors hover:border-blue-300"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() => navigate(`/candidates/${c.candidate_id}`)}
                          className="min-w-0 text-left"
                        >
                          <p className="truncate text-[13px] text-slate-900 group-hover:text-blue-700">{c.name}</p>
                          <p className="truncate text-[11px] text-slate-500">{c.designation || "Role not available"}</p>
                        </button>
                        {canMove && (
                          <Checkbox
                            checked={selected.includes(c.application_id)}
                            onCheckedChange={(v) =>
                              setSelected((s) =>
                                v ? [...s, c.application_id] : s.filter((x) => x !== c.application_id)
                              )
                            }
                            data-testid="pipeline-card-checkbox"
                            className="mt-0.5 border-slate-300"
                          />
                        )}
                      </div>
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                        {c.experience != null && <span className="font-mono-num">{c.experience} yrs</span>}
                        {c.location && <span className="truncate">{c.location.split(",")[0]}</span>}
                        {c.notice_period && <span>{c.notice_period}</span>}
                      </div>
                      <div className="mt-2.5 flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {(c.skills || []).slice(0, 2).map((s) => (
                            <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-500">
                              {s}
                            </span>
                          ))}
                        </div>
                        {c.match_score != null && (
                          <span
                            className="font-mono-num rounded px-1.5 py-0.5 text-[10px]"
                            style={{
                              color: c.match_score >= 75 ? "#34d399" : c.match_score >= 55 ? "#38bdf8" : "#fbbf24",
                              background: "rgba(255,255,255,0.03)",
                            }}
                          >
                            {c.match_score}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
      {canMove && (
        <p className="text-[11px] text-slate-400">
          Drag a card between columns to change stage, or select several cards for a bulk move. Every move is logged.
        </p>
      )}
    </div>
  );
}

function AddCandidatesDialog({ open, onOpenChange, jobId, onAdded }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    api.get(`/ai/job/${jobId}/ranked-candidates`, { params: { limit: 25 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [open, jobId]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/candidates/bulk?action=add_to_job", {
        candidate_ids: selected, job_id: jobId, stage: "New",
      });
      toast.success(`Added ${selected.length} candidate(s) to the pipeline`);
      onOpenChange(false);
      onAdded?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium text-slate-900">
            <Sparkles className="h-4 w-4 text-violet-600" /> AI-ranked candidates for this job
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[58vh] space-y-2 overflow-y-auto pr-1 thin-scroll">
          {!data ? (
            Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 bg-slate-100" />)
          ) : (
            data.items.map((c) => (
              <label
                key={c.candidate_id}
                data-testid="rank-candidate-row"
                className={`flex cursor-pointer items-center gap-3 rounded border p-3 ${
                  c.already_in_pipeline ? "border-slate-200 opacity-50" : "border-slate-200 hover:border-blue-300"
                }`}
              >
                <Checkbox
                  disabled={c.already_in_pipeline}
                  checked={selected.includes(c.candidate_id)}
                  onCheckedChange={(v) =>
                    setSelected((s) => (v ? [...s, c.candidate_id] : s.filter((x) => x !== c.candidate_id)))
                  }
                  className="border-slate-300"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-900">
                    {c.name}
                    {c.already_in_pipeline && <span className="ml-2 text-[10px] text-slate-500">already in pipeline</span>}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {c.designation || "Role not available"} · {c.company || "Company not available"} ·{" "}
                    {c.experience ?? "?"} yrs · {c.location || "Location not available"}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {c.matched_skills.slice(0, 4).map((s) => (
                      <SkillChip key={s} skill={s} tone="matched" />
                    ))}
                    {c.missing_skills.slice(0, 2).map((s) => (
                      <SkillChip key={s} skill={s} tone="missing" />
                    ))}
                  </div>
                </div>
                <MatchRing score={c.score} size={52} label="" />
              </label>
            ))
          )}
        </div>
        <p className="text-[11px] text-violet-700/80">
          Ranking is computed from structured profile data. It is a suggestion, not a hiring decision.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={add} disabled={!selected.length || busy} data-testid="add-candidates-confirm" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            Add {selected.length || ""} to pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isStaff, user } = useAuth();
  const [job, setJob] = useState(null);
  const [edit, setEdit] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [ivOpen, setIvOpen] = useState(false);
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      setJob(data);
    } catch (e) {
      toast.error(apiError(e, "Job not found"));
      navigate("/jobs");
    }
  }, [jobId, navigate]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/notes`, { body: note });
      setNote("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const genSummary = async () => {
    setSummary("loading");
    try {
      const { data } = await api.post("/ai/summary", { job_id: jobId });
      setSummary(data.summary);
    } catch (e) {
      setSummary(null);
      toast.error(apiError(e));
    }
  };

  if (!job)
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 bg-slate-100" />
        <Skeleton className="h-32 bg-slate-100" />
        <Skeleton className="h-80 bg-slate-100" />
      </div>
    );

  const canMove = ["super_admin", "admin", "recruitment_manager", "recruiter", "sourcer", "client"].includes(user?.role);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/jobs")} data-testid="back-to-jobs" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-600">
        <ArrowLeft className="h-3.5 w-3.5" /> All jobs
      </button>

      <PageHeader
        meta={`${job.job_code} · ${job.department || "Engineering"}`}
        title={job.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> {job.client_name || "No client linked"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {job.location || "Not available"} · {job.work_mode}
            </span>
            <span className="font-mono-num">
              {job.min_experience ?? "?"}–{job.max_experience ?? "?"} yrs
            </span>
            <span className="font-mono-num">
              {job.min_ctc ?? "?"}–{job.max_ctc ?? "?"} LPA
            </span>
            <span>{job.openings} opening{job.openings > 1 ? "s" : ""}</span>
          </span>
        }
        actions={
          <>
            <StatusBadge value={job.priority} />
            <StatusBadge value={job.status} testid="job-detail-status" />
            {isStaff && (
              <>
                <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="job-add-candidates-button"
                  className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                  <Plus className="h-3.5 w-3.5" /> Add candidates
                </Button>
                <Button variant="outline" onClick={() => setEdit(true)} data-testid="job-edit-button"
                  className="h-9 gap-2 rounded-full border-slate-200 bg-transparent text-xs text-slate-600 hover:bg-slate-100">
                  <Edit3 className="h-3.5 w-3.5" /> Edit
                </Button>
              </>
            )}
          </>
        }
      />

      <section className="surface rounded-md p-6">
        <p className="label-xs">Hiring funnel</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {FUNNEL.map((f, i) => {
            const base = job.funnel?.Applications || 1;
            const val = job.funnel?.[f] ?? 0;
            return (
              <div key={f} className="rounded border border-slate-200 bg-slate-50 p-3" data-testid={`funnel-${f}`}>
                <p className="label-xs">{f}</p>
                <p className="font-mono-num mt-2 text-2xl text-slate-900">{val}</p>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, (val / base) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Tabs defaultValue="pipeline">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
          {["pipeline", "details", "notes", "activity"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`job-tab-${t}`} className="rounded px-4 py-1.5 text-xs capitalize data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="pipeline" className="mt-5">
          <PipelineBoard jobId={jobId} canMove={canMove} onChanged={() => setRefresh((r) => r + 1)} />
        </TabsContent>

        <TabsContent value="details" className="mt-5 space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="surface space-y-5 rounded-md p-6 lg:col-span-2">
              <div>
                <p className="label-xs mb-2">Job description</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">
                  {job.description || "Not available"}
                </p>
              </div>
              <div>
                <p className="label-xs mb-2">Responsibilities</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">
                  {job.responsibilities || "Not available"}
                </p>
              </div>
              <div>
                <p className="label-xs mb-2">Requirements</p>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">
                  {job.requirements || "Not available"}
                </p>
              </div>
              {job.custom_note && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="job-custom-note-display">
                  <p className="label-xs mb-2 text-amber-800">Internal note · team only</p>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-amber-900/90">
                    {job.custom_note}
                  </p>
                </div>
              )}
              <div className="border-t border-slate-200 pt-5">
                <div className="flex items-center justify-between">
                  <p className="label-xs">AI job summary</p>
                  <Button size="sm" variant="ghost" onClick={genSummary} data-testid="job-ai-summary-button" className="h-7 gap-1.5 text-[11px] text-violet-700 hover:bg-violet-50">
                    <Sparkles className="h-3 w-3" /> Generate
                  </Button>
                </div>
                {summary === "loading" ? (
                  <Skeleton className="mt-3 h-16 bg-slate-100" />
                ) : summary ? (
                  <p className="mt-3 rounded border border-violet-200 bg-violet-50 p-3 text-sm leading-relaxed text-slate-600" data-testid="job-ai-summary">
                    {summary}
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-slate-400">
                    Generate a short AI summary of this requirement, built only from the fields above.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="surface space-y-4 rounded-md p-5">
                <p className="label-xs">Required skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {(job.required_skills || []).map((s) => <SkillChip key={s} skill={s} />)}
                </div>
                <p className="label-xs pt-2">Preferred skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {(job.preferred_skills || []).length
                    ? job.preferred_skills.map((s) => <SkillChip key={s} skill={s} />)
                    : <span className="text-xs text-slate-400">Not available</span>}
                </div>
              </div>
              <div className="surface grid gap-4 rounded-md p-5 sm:grid-cols-2">
                <Field label="Recruiter" value={job.recruiter_name} />
                <Field label="Sourcer" value={job.sourcer_name} />
                <Field label="Hiring manager" value={job.hiring_manager_name} />
                <Field label="Employment type" value={job.employment_type} />
                <Field label="Education" value={job.education} />
                <Field label="Notice period" value={job.notice_period} />
                <Field label="Opened" value={fmtDate(job.opening_date)} />
                <Field label="Closing" value={fmtDate(job.closing_date)} />
                <Field label="SLA" value={job.sla_days ? `${job.sla_days} days` : null} mono />
                <Field label="Industry" value={job.industry} />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-5">
          <div className="surface space-y-4 rounded-md p-6">
            {isStaff && (
              <div className="space-y-3">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a recruiter note about this requirement…"
                  data-testid="job-note-input"
                  className="border-slate-200 bg-slate-50 text-sm text-slate-800"
                />
                <Button size="sm" onClick={addNote} data-testid="job-note-submit" className="h-8 gap-2 rounded-full bg-blue-600 text-xs hover:bg-blue-500">
                  <StickyNote className="h-3 w-3" /> Add note
                </Button>
              </div>
            )}
            {(job.notes || []).length === 0 ? (
              <EmptyState icon={StickyNote} title="No notes yet" description="Capture client conversations and requirement clarifications here." />
            ) : (
              <ul className="divide-y divide-slate-200">
                {[...job.notes].reverse().map((n) => (
                  <li key={n.id} className="py-4" data-testid="job-note-item">
                    <p className="text-sm text-slate-600">{n.body}</p>
                    <p className="mt-1.5 text-[11px] text-slate-400">
                      {n.author_name} · {relativeTime(n.created_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-5">
          <div className="surface rounded-md p-6">
            {(job.activity || []).length === 0 ? (
              <EmptyState icon={Users} title="No activity recorded" description="Stage moves and edits will appear here." />
            ) : (
              <ul className="space-y-4">
                {job.activity.map((a) => (
                  <li key={a.id} className="flex gap-3" data-testid="job-activity-item">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                    <div>
                      <p className="text-xs text-slate-600">
                        <span className="text-slate-900">{a.user_name}</span> {a.action}
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-400">{relativeTime(a.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <JobForm open={edit} onOpenChange={setEdit} job={job} onSaved={() => setRefresh((r) => r + 1)} />
      <AddCandidatesDialog open={addOpen} onOpenChange={setAddOpen} jobId={jobId} onAdded={() => setRefresh((r) => r + 1)} />
      <InterviewForm open={ivOpen} onOpenChange={setIvOpen} defaults={{ job_id: jobId }} />
    </div>
  );
}
